/**
 * API 응답 기본 구조 클래스
 * - 모든 API 응답을 표준화
 * - 제네릭 타입 지원
 * - 일관된 에러 처리
 */
export class BaseResponse<T = unknown> {
  resultCode: number;
  resultMsg: string;
  data: T | null;
  success: boolean;
  errors: string[];
  timestamp: string;

  constructor(
    resultCode: number = 200,
    resultMsg: string = '',
    data: T | null = null,
    success: boolean = true,
    errors: string[] = []
  ) {
    this.resultCode = resultCode;
    this.resultMsg = resultMsg;
    this.data = data;
    this.success = success;
    this.errors = errors;
    this.timestamp = new Date().toISOString();
  }

  /**
   * 성공 응답 생성
   */
  static success<T>(
    data: T | null = null,
    resultMsg: string = '성공',
    resultCode: number = 200
  ): BaseResponse<T> {
    return new BaseResponse<T>(resultCode, resultMsg, data, true, []);
  }

  /**
   * 실패 응답 생성
   */
  static error<T = null>(
    resultMsg: string = '오류가 발생했습니다',
    resultCode: number = 500,
    errors: string[] = []
  ): BaseResponse<T> {
    return new BaseResponse<T>(resultCode, resultMsg, null, false, errors);
  }

  /**
   * API 응답에서 지정된 필드의 데이터를 추출하여 BaseResponse로 변환
   */
  static fromApiResponse<T>(
    apiResponse: Record<string, unknown>,
    dataField: string = 'data',
    resultMsgField: string = 'resultMsg',
    resultCodeField: string = 'resultCode'
  ): BaseResponse<T> {
    try {
      const resultCode = (apiResponse[resultCodeField] as number) || 200;
      const resultMsg = (apiResponse[resultMsgField] as string) || '성공';
      const data =
        (apiResponse[dataField] as T) || (apiResponse.data as T) || null;
      const success = resultCode >= 200 && resultCode < 300;

      return new BaseResponse<T>(resultCode, resultMsg, data, success, []);
    } catch (error) {
      return BaseResponse.error<T>(
        'API 응답 파싱 실패: ' + (error as Error).message
      );
    }
  }

  /**
   * 검증 실패 응답 생성
   */
  static validationError(
    errors: string[] = [],
    message: string = '입력값이 올바르지 않습니다'
  ): BaseResponse<null> {
    return new BaseResponse<null>(400, message, null, false, errors);
  }

  /**
   * 인증 실패 응답 생성
   */
  static unauthorized(
    message: string = '인증이 필요합니다'
  ): BaseResponse<null> {
    return new BaseResponse<null>(401, message, null, false, []);
  }

  /**
   * 권한 없음 응답 생성
   */
  static forbidden(message: string = '권한이 없습니다'): BaseResponse<null> {
    return new BaseResponse<null>(403, message, null, false, []);
  }

  /**
   * 찾을 수 없음 응답 생성
   */
  static notFound(
    message: string = '요청한 리소스를 찾을 수 없습니다'
  ): BaseResponse<null> {
    return new BaseResponse<null>(404, message, null, false, []);
  }

  /**
   * 응답이 성공인지 확인
   */
  isSuccess(): boolean {
    return this.success && this.resultCode >= 200 && this.resultCode < 300;
  }

  /**
   * 응답이 실패인지 확인
   */
  isError(): boolean {
    return !this.success || this.resultCode >= 400;
  }

  /**
   * 클라이언트 에러인지 확인 (4xx)
   */
  isClientError(): boolean {
    return this.resultCode >= 400 && this.resultCode < 500;
  }

  /**
   * 서버 에러인지 확인 (5xx)
   */
  isServerError(): boolean {
    return this.resultCode >= 500;
  }

  /**
   * 데이터 존재 여부 확인
   */
  hasData(): boolean {
    return this.data !== null && this.data !== undefined;
  }

  /**
   * 에러 존재 여부 확인
   */
  hasErrors(): boolean {
    return this.errors && this.errors.length > 0;
  }

  /**
   * JSON 형태로 변환
   */
  toJSON(): Record<string, unknown> {
    return {
      resultCode: this.resultCode,
      resultMsg: this.resultMsg,
      data: this.data,
      success: this.success,
      errors: this.errors,
      timestamp: this.timestamp,
    };
  }
}

/**
 * API 응답을 BaseResponse로 변환하는 헬퍼 함수
 */
export function toBaseResponse<T>(apiResponse: unknown): BaseResponse<T> {
  if (apiResponse instanceof BaseResponse) {
    return apiResponse as BaseResponse<T>;
  }

  if (apiResponse && typeof apiResponse === 'object') {
    const response = apiResponse as Record<string, unknown>;
    const {
      resultCode = 200,
      resultMsg = '성공',
      success = true,
      errors = [],
    } = response;

    // 다양한 데이터 필드명을 data로 자동 변환
    let data: T | null = null;

    const dataFields = ['data', 'list', 'items', 'item', 'result'];
    for (const field of dataFields) {
      if (response[field] !== undefined) {
        data = response[field] as T;
        break;
      }
    }

    return new BaseResponse<T>(
      resultCode as number,
      resultMsg as string,
      data,
      success as boolean,
      errors as string[]
    );
  }

  return BaseResponse.success<T>(apiResponse as T);
}
